import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useUser } from './user-provider';
import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';

interface FeaturesContextType {
    isLoading: boolean;
    // True once this entity's features have been fetched at least once -
    // lets a page tell "not available" apart from "not loaded yet".
    isLoaded: boolean;
    isEnabled: (featureKey: string) => boolean;
    isVisibleInNav: (featureKey: string) => boolean;
    reload: () => void;
}

const FeaturesContext = createContext<FeaturesContextType>({
    isLoading: false,
    isLoaded: false,
    isEnabled: () => true,
    isVisibleInNav: () => false, // hidden until explicitly enabled
    reload: () => {},
});

export const FeaturesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useUser();
    const [features, setFeatures] = useState<Record<string, boolean>>({});
    const [isLoaded, setIsLoaded] = useState(false);
    const [result, setConfig] = useApi<any>();

    const load = useCallback(() => {
        if (!user?.entityId) return;
        setConfig(
            buildApiRequestConfig(routes.features.getByEntity, [{ param: 'entityId', value: user.entityId }])
        );
    }, [user?.entityId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const data = result.data;
        if (data?.success) {
            setFeatures(data.features ?? {});
            setIsLoaded(true);
        }
    }, [result.data]);

    const isEnabled = useCallback((featureKey: string): boolean => {
        // No flag row = unrestricted (used for page/API guards)
        if (!(featureKey in features)) return true;
        return features[featureKey];
    }, [features]);

    const isVisibleInNav = useCallback((featureKey: string): boolean => {
        // No flag row = hidden; must be explicitly enabled for the entity's country
        return features[featureKey] === true;
    }, [features]);

    return (
        <FeaturesContext.Provider value={{ isLoading: result.isLoading, isLoaded, isEnabled, isVisibleInNav, reload: load }}>
            {children}
        </FeaturesContext.Provider>
    );
};

export const useFeatures = () => useContext(FeaturesContext);
